import { useEffect, useState } from 'react';
import { admin } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import type { FanMembership } from '../types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

interface Props {
  teamId: string;
  className?: string;
}

/**
 * Three states:
 *  - Not signed in: link to /login
 *  - Signed in, no follow: "Follow" button → POST /follow
 *  - Signed in with status: badge ("Pending" / "Approved" / "Denied")
 */
export default function FollowButton({ teamId, className }: Props) {
  const { ready, user } = useAuth();
  const [membership, setMembership] = useState<FanMembership | null | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!user) {
      setMembership(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const m = await admin.getMyFollow(teamId);
        if (!cancelled) setMembership(m);
      } catch {
        if (!cancelled) setMembership(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, user]);

  if (!ready) return null;

  if (!user) {
    return (
      <Button
        href={`/login?from=${encodeURIComponent(`/teams/${teamId}`)}`}
        outline
        className={className}
      >
        Sign in to follow
      </Button>
    );
  }

  if (membership === undefined) return null; // loading

  if (membership === null) {
    return (
      <Button
        outline
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(undefined);
          try {
            const m = await admin.followTeam(teamId);
            setMembership(m);
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
          } finally {
            setBusy(false);
          }
        }}
        className={className}
      >
        {busy ? 'Following…' : 'Follow team'}
      </Button>
    );
  }

  const color =
    membership.status === 'APPROVED'
      ? 'lime'
      : membership.status === 'PENDING'
        ? 'amber'
        : 'rose';

  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      <Badge color={color}>
        {membership.status === 'APPROVED'
          ? 'Following'
          : membership.status === 'PENDING'
            ? 'Pending approval'
            : 'Denied'}
      </Badge>
      {error ? (
        <span className="text-xs text-red-400">{error}</span>
      ) : null}
    </div>
  );
}
